import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CommentThread from './CommentThread';
import DodChecklist from './DodChecklist';
import ScopeDetailContent from './ScopeDetailContent';
import ScopeTodoList from './ScopeTodoList';
import useEntityComments from './useEntityComments';
import { markCommentsRead } from '../../utils/entityCommentsApi';

// The pane is comfortable on its own; opening comments widens the drawer so the
// thread sits beside it rather than shoving the pane off screen.
const NARROW = 460;
const WIDE = 860;
const COMMENTS_WIDTH = 360;

/**
 * One "dog-ear" tab: a folded ear rounded on its page-facing (left) edge, filled
 * when active. Details and Todos select a pane; Comments toggles the second
 * column open beside whichever pane is showing.
 */
const Ear = ({ icon, label, active, onClick, indicator = false }) => (
  <Tooltip title={label} placement="left">
    <Box
      role="tab"
      aria-selected={active}
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        py: 1.25,
        cursor: 'pointer',
        color: active ? 'primary.contrastText' : 'text.secondary',
        bgcolor: active ? 'primary.main' : 'action.hover',
        borderTopLeftRadius: 10,
        borderBottomLeftRadius: 10,
        transition: 'background-color 0.15s ease, color 0.15s ease',
        '&:hover': { bgcolor: active ? 'primary.main' : 'action.selected' },
      }}
    >
      <Badge
        color="error"
        variant="dot"
        overlap="circular"
        invisible={!indicator}
      >
        {icon}
      </Badge>
      <Typography
        variant="caption"
        sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontWeight: active ? 700 : 500 }}
      >
        {label}
      </Typography>
    </Box>
  </Tooltip>
);

/**
 * Right-anchored drawer for a single scope. A dog-ear rail down its page-facing
 * edge switches the primary pane between the scope's details and its todos, and
 * opens a comment column beside them. Every affordance the old inline detail bar
 * offered lives here now, so a hill dot and a scope card open the same place.
 */
const ScopeDrawer = ({
  scope,
  open,
  onClose,
  onClearOverride,
  onTogglePark,
  onExclude,
  onRename,
  onHammer,
  onRestate,
  onChangeStatus,
  onChangeAssignee,
  onScopeChanged,
  manual = false,
  readOnly = false,
  attachmentsEnabled = false,
  statusOptions = [],
  definitionOfDone = [],
  dodStandard = '',
  preloadedTodos = null,
  moveTargets = [],
  onTodosChanged,
  onMoveTodo,
  onReturnToScratchpad,
  movingKey = null,
  onCommentsRead,
  members = [],
  onChangeAssigneeMember,
}) => {
  const [activeTab, setActiveTab] = useState('todos');
  const [commentsOpen, setCommentsOpen] = useState(false);

  // A fresh scope starts on its todos with comments closed, rather than
  // carrying the last scope's view along.
  useEffect(() => {
    setActiveTab('todos');
    setCommentsOpen(false);
  }, [scope?.id]);

  // Fetch the scope's comment count so the Comments ear can flag a discussion
  // without the reader having to open the column. The hook loads over REST, so
  // the dot appears even when the live socket is down.
  const { count: commentCount } = useEntityComments('scope', scope?.id || null);

  // Opening the Comments column is what "reads" the thread: advance this user's
  // read watermark on the server and clear the card's dot optimistically. The
  // per-scope reset effect above sets commentsOpen back to false, so re-opening
  // the same scope re-fires this.
  useEffect(() => {
    if (commentsOpen && scope?.id) {
      markCommentsRead('scope', scope.id).catch(() => {});
      onCommentsRead?.(scope.id);
    }
  }, [commentsOpen, scope?.id, onCommentsRead]);

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      keepMounted={false}
      ModalProps={{ disableScrollLock: true }}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: commentsOpen ? WIDE : NARROW },
          maxWidth: '100vw',
          transition: 'width .2s ease',
          display: 'flex',
          flexDirection: 'row',
        },
      }}
    >
      {!scope ? null : (
        <>
          {/* Dog-ear rail pinned to the page-facing edge. */}
          <Box
            role="tablist"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              pt: 2,
              pl: 0.5,
              flexShrink: 0,
              width: 44,
            }}
          >
            <Ear
              icon={<DescriptionOutlinedIcon fontSize="small" />}
              label="Details"
              active={activeTab === 'details'}
              onClick={() => setActiveTab('details')}
            />
            <Ear
              icon={<ChecklistIcon fontSize="small" />}
              label="Todos"
              active={activeTab === 'todos'}
              onClick={() => setActiveTab('todos')}
            />
            <Ear
              icon={<ChatBubbleOutlineIcon fontSize="small" />}
              label="Comments"
              active={commentsOpen}
              indicator={!commentsOpen && commentCount > 0}
              onClick={() => setCommentsOpen((v) => !v)}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: { xs: commentsOpen ? 'column' : 'row', sm: 'row' },
            }}
          >
            {/* Primary pane: Details or Todos. */}
            <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                    {scope.jiraSummary || scope.jiraKey}
                  </Typography>
                  {scope.jiraUrl && (
                    <Link
                      href={scope.jiraUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}
                    >
                      {scope.jiraKey}
                      <OpenInNewIcon sx={{ fontSize: 13 }} />
                    </Link>
                  )}
                </Box>
                <IconButton size="small" onClick={onClose} aria-label="Close scope details">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {activeTab === 'details' ? (
                <ScopeDetailContent
                  scope={scope}
                  onClearOverride={onClearOverride}
                  onTogglePark={onTogglePark}
                  onExclude={onExclude}
                  onRename={onRename}
                  onHammer={onHammer}
                  onRestate={onRestate}
                  onChangeStatus={onChangeStatus}
                  onChangeAssignee={onChangeAssignee}
                  onScopeChanged={onScopeChanged}
                  manual={manual}
                  statusOptions={statusOptions}
                  readOnly={readOnly}
                  members={members}
                  onChangeAssigneeMember={onChangeAssigneeMember}
                />
              ) : (
                <>
                  <ScopeTodoList
                    scope={scope}
                    manual={manual}
                    readOnly={readOnly}
                    attachmentsEnabled={attachmentsEnabled}
                    preloadedTodos={preloadedTodos}
                    onTodosChanged={onTodosChanged}
                    onDropTodo={onMoveTodo}
                    onReturnToScratchpad={onReturnToScratchpad}
                    moveTargets={moveTargets}
                    movingKey={movingKey}
                    members={members}
                  />
                  <Divider sx={{ my: 2 }} />
                  <DodChecklist
                    scope={scope}
                    items={definitionOfDone}
                    standard={dodStandard}
                    readOnly={readOnly}
                    onScopeChanged={onScopeChanged}
                  />
                </>
              )}
            </Box>

            {/* Comments column, beside the pane when open. */}
            {commentsOpen && (
              <Box
                sx={{
                  width: { xs: '100%', sm: COMMENTS_WIDTH },
                  flexShrink: 0,
                  overflowY: 'auto',
                  p: 2,
                  borderLeft: { sm: '1px solid' },
                  borderTop: { xs: '1px solid', sm: 'none' },
                  borderColor: { xs: 'divider', sm: 'divider' },
                }}
              >
                <CommentThread
                  key={scope.id}
                  commentableType="scope"
                  commentableId={scope.id}
                  defaultExpanded
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default ScopeDrawer;
